import { useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";

import {
  EstadosOrdenTrabajoEnumChoice,
  TABLE_CONSTANTS_COLUMS,
  useTableFilter,
  useTableServerSideFiltering,
} from "@/shared";
import { useFetchOrdenTrabajos } from "@/shared/actions/erp";
import { GeneralTable } from "@/shared/components/ui";
import { OrdenTrabajo } from "@/shared/interfaces";
import { useTableColumnsTrasladosFinalizados } from "../../hooks/useTableColumnsTrasladosFinalizados";
import TrasladoFinalizadoDetalle from "./TrasladoFinalizadoDetalle";

/**
 * Tab "Finalizados" de traslados: lista las OT de tipo TRASLADO en estado
 * REALIZADA y abre un dossier full con toda la trazabilidad del proceso
 * (tiempos, técnico, activaciones, recoordinación, valores/recaudación y fotos).
 */
export default function TrasladosFinalizadosTab() {
  const { pagination, setPagination } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  const { columnFilters, setColumnFilters } = useTableServerSideFiltering();

  const { columns } = useTableColumnsTrasladosFinalizados();

  const [selected, setSelected] = useState<OrdenTrabajo | null>(null);

  const { data, isLoading, isRefetching } = useFetchOrdenTrabajos({
    enabled: true,
    params: {
      page: pageIndex + 1,
      page_size: pageSize,
      tipo_orden_servicio: "TRASLADO",
      estado_orden_trabajo: EstadosOrdenTrabajoEnumChoice.REALIZADA,
    } as any,
  });

  return (
    <>
      <GeneralTable<OrdenTrabajo>
        columns={columns}
        data={data?.data?.data || []}
        isLoading={isLoading}
        isRefetching={isRefetching}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        enableManualFiltering={true}
        enableGlobalFilter={false}
        pagination={pagination}
        onPaging={setPagination}
        rowCount={data?.data?.count}
        enableColumnActions={true}
        enableEditing={true}
        canEdit={false}
        showCustomActionBtnSpace={true}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.COLUMN_WIDTH_SMALL}
        customActionBtnSpace={(row: OrdenTrabajo) => (
          <Tooltip title="Ver detalle del traslado finalizado">
            <IconButton onClick={() => setSelected(row)}>
              <VisibilityIcon color="info" />
            </IconButton>
          </Tooltip>
        )}
      />

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Stack spacing={0.25}>
            <Typography variant="h6" fontWeight={700}>
              Detalle de traslado finalizado
            </Typography>
            {selected && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {selected?.prospecto_data?.razon_social ||
                    selected?.numero_referencia}
                </Typography>
                <Chip
                  size="small"
                  label={selected?.estado_orden_trabajo}
                  color="success"
                  variant="outlined"
                />
              </Stack>
            )}
          </Stack>
          <IconButton
            onClick={() => setSelected(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1 }}>
            {selected && <TrasladoFinalizadoDetalle ot={selected} />}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
